import {
  downloadJobsExport,
  useJobChainFilters,
  useJobs,
  useJobStatus,
  useJobTypes,
} from '@/api/Jobs';
import { useTranslate } from '@/translations';
import { AppSearch, JobListData, JobTypeEnum } from '@/types';
import {
  CustomColumns,
  CustomTable,
  VehicleInformation,
} from '@/ui/components';
import { AppRoutes } from '@/routes';
import { StorageKeys } from '@/utils/constants';
import { timeConverter } from '@/utils/helpers';
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

export const JobsDataGrid = () => {
  const { t } = useTranslate();
  const defaultAppSearch: AppSearch<JobListData> = useMemo(
    () => ({
      name: undefined,
      page: 1,
      pageSize: 10,
      filters: [],
      sortField: 'createdAt',
      sortOrder: 'descend',
    }),
    [],
  );
  const [query, setQuery] = useState<AppSearch<JobListData> | undefined>(
    undefined,
  );

  const { data: jobsData, isFetching: isJobsLoading } = useJobs(query);
  const { data: filterOptions, isFetching: filtersLoading } =
    useJobChainFilters();
  const { data: jobTypes, isFetching: isJobTypesLoading } = useJobTypes();
  const { data: jobStatus, isFetching: isJobStatusLoading } = useJobStatus();

  const isLoading =
    isJobsLoading || filtersLoading || isJobTypesLoading || isJobStatusLoading;

  const columns: CustomColumns<JobListData> = useMemo(
    () => [
      {
        dataIndex: 'jobId',
        title: 'Job ID',
        exportRender: (value) =>
          value === undefined || value === null ? '' : String(value),
        width: 110,
        fixed: 'left',
        sortable: true,
      },
      {
        dataIndex: 'jobChainId',
        title: 'Chain ID',
        render: (value) => (
          <Link to={`${AppRoutes.jobs}/${value}`}>{value}</Link>
        ),
        exportRender: (value) =>
          value === undefined || value === null ? '' : String(value),
        width: 110,
        sortable: true,
        filter: true,
      },
      {
        dataIndex: 'vin',
        title: t('vin'),
        render: (value) =>
          value ? <VehicleInformation vin={value} showAsLabel /> : '—',
        exportRender: (value) =>
          value === undefined || value === null ? '' : String(value),
        sortable: true,
        filter: true,
        width: 200,
        fixed: 'left',
      },
      {
        dataIndex: 'locationId',
        title: t('location'),
        renderType: 'select',
        render: (_, data) => data.location || '-',
        exportRender: (_, record) => record.location || '',
        filter: true,
        filterOptions: filterOptions?.locations,
        width: 180,
      },
      {
        dataIndex: 'jobType',
        title: 'Type',
        renderType: 'select',
        render: (_, data) => data.jobTypeDescription,
        exportRender: (_, record) => record.jobTypeDescription ?? '',
        sortable: true,
        filter: true,
        filterOptions: jobTypes?.map((item) => ({
          value: item.value as JobTypeEnum,
          text: item.text,
        })),
        width: 180,
      },
      {
        dataIndex: 'jobChainType',
        title: 'Chain Type',
        renderType: 'select',
        render: (_, data) => data.jobChainTypeDescription,
        exportRender: (_, record) => record.jobChainTypeDescription ?? '',
        sortable: true,
        filter: true,
        filterOptions: filterOptions?.types,
        width: 220,
      },
      {
        dataIndex: 'jobStatusId',
        title: 'Status',
        renderType: 'select',
        render: (_, data) => data.jobStatus,
        exportRender: (_, record) => record.jobStatus ?? '',
        sortable: true,
        filter: true,
        filterOptions: jobStatus?.map((item) => ({
          value: item.jobStatusId,
          text: item.description || item.name,
        })),
        width: 150,
        align: 'center',
      },
      {
        dataIndex: 'customerId',
        title: 'Customer',
        renderType: 'select',
        render: (_, data) => data.customerName,
        exportRender: (_, record) => record.customerName ?? '',
        sortable: true,
        filter: true,
        filterOptions: filterOptions?.customers,
        width: 200,
      },
      {
        dataIndex: 'subCustomerId',
        title: 'Sub Customer',
        renderType: 'select',
        render: (_, data) => data.subCustomerName,
        exportRender: (_, record) => record.subCustomerName ?? '',
        sortable: true,
        filter: true,
        filterOptions: filterOptions?.subCustomers || [],
        width: 200,
      },
      {
        dataIndex: 'notes',
        title: t('notes'),
        sortable: true,
        filter: true,
        width: 200,
      },
      {
        dataIndex: 'createdAt',
        title: 'Create Date',
        renderType: 'date',
        width: 180,
        sortable: true,
        filter: true,
        render: (value) => timeConverter(value),
        exportRender: (value) => timeConverter(value) || '',
      },
      {
        dataIndex: 'createdBy',
        title: 'Created By',
        sortable: true,
        filter: true,
        width: 180,
      },
      {
        dataIndex: 'completedAt',
        title: 'Complete Date',
        renderType: 'date',
        width: 180,
        sortable: true,
        filter: true,
        render: (value) => timeConverter(value),
        exportRender: (value) => timeConverter(value) || '',
      },
      {
        dataIndex: 'completedBy',
        title: 'Completed By',
        sortable: true,
        filter: true,
        width: 180,
      },
    ],
    [filterOptions, jobStatus, jobTypes, t],
  );

  const onAppSearchChange = useCallback(
    (search: AppSearch<JobListData>) => setQuery(search),
    [],
  );

  const appSearchOptions = useMemo(
    () => ({
      storageKey: StorageKeys.SearchCompoundJobs,
      defaultSearch: defaultAppSearch,
      value: query,
      onChange: onAppSearchChange,
    }),
    [defaultAppSearch, onAppSearchChange, query],
  );

  return (
    <CustomTable
      dataSource={jobsData?.entities || []}
      columns={columns}
      rowKey="jobId"
      loading={isLoading}
      pagination={{
        total: jobsData?.totalCount || 0,
        pageSize: query?.pageSize,
        current: query?.page || 1,
      }}
      exportOptions={{
        fileName: 'Jobs',
        skipClientExport: true,
        formats: ['xlsx', 'csv'],
        formatLabels: { csv: 'CSV (zip)' },
        getExportData: async (type) => {
          if (!query) return [];
          await downloadJobsExport(query, type === 'csv' ? 'csv' : 'xlsx');
          return [];
        },
      }}
      appSearchOptions={appSearchOptions}
      scroll={{ x: '100%' }}
    />
  );
};
